/**
 * Controls information density in the website UI only. Experience mode must
 * never alter a backtest request, account entitlement, saved strategy, or
 * web-to-app handoff payload.
 */
export const experienceModes = ["guided", "focused"] as const;

export type ExperienceMode = (typeof experienceModes)[number];

export const EXPERIENCE_MODE_STORAGE_KEY = "backtest.web.experienceMode";

export interface ExperienceModeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const DEFAULT_EXPERIENCE_MODE: ExperienceMode = "guided";

function isExperienceMode(value: unknown): value is ExperienceMode {
  return value === "focused" || value === "guided";
}

function browserStorage(): ExperienceModeStorage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** Invalid, missing, and future values fail closed to the educational view. */
export function resolveExperienceMode(value: unknown): ExperienceMode {
  return isExperienceMode(value) ? value : DEFAULT_EXPERIENCE_MODE;
}

export function loadExperienceMode(
  storage: ExperienceModeStorage | null | undefined = browserStorage(),
): ExperienceMode {
  if (!storage) return DEFAULT_EXPERIENCE_MODE;
  try {
    return resolveExperienceMode(storage.getItem(EXPERIENCE_MODE_STORAGE_KEY));
  } catch {
    return DEFAULT_EXPERIENCE_MODE;
  }
}

export function hasStoredExperienceMode(
  storage: ExperienceModeStorage | null | undefined = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    const value = storage.getItem(EXPERIENCE_MODE_STORAGE_KEY);
    return isExperienceMode(value);
  } catch {
    return false;
  }
}

export function saveExperienceMode(
  storage: ExperienceModeStorage | null | undefined,
  mode: ExperienceMode,
): boolean {
  if (!storage || !isExperienceMode(mode)) return false;
  try {
    storage.setItem(EXPERIENCE_MODE_STORAGE_KEY, mode);
    return true;
  } catch {
    return false;
  }
}
